import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import { InputHandler } from '@componentsApp/common';
import { APIS } from '@constantsApp/index';
import {
  generateRegexValidators,
  INPUT_REQUIRED
} from '@constantsApp/inputRules';
import { tnl } from '@i18nApp/i18n';
import { getSelectQuestionOptions } from '@pagesApp/Private/WorkUnit/Surveys/SurveysRequests';
import {
  EMPTY_SELECT,
  formatDateForInput,
  getFormattedDefaultValue,
  presignedFile
} from '@utilsApp/index';
import { processFormula } from './SurveyQuestionsData';

const SurveyQuestions = ({
  questionsData = [],
  surveyForm,
  responseMapper = {},
  responseFormulaMapper = {},
  preloadedFile = {},
  historySelected = null,
  setResponseMapper = () => {},
  setResponseFormulaMapper = () => {},
  setFillingSurvey = () => {},
  setPreloadedFile = () => {}
}) => {
  const [optionsInput, setOptionsInput] = useState([EMPTY_SELECT]);
  const [loadingPresigned, setLoadingPresigned] = useState(false);

  const processMinMaxValue = (value) => {
    if (value?.toLowerCase() === 'auto_now') {
      return formatDateForInput(new Date());
    }
    return value;
  };

  return (
    <div className="row">
      {questionsData.map((question) => {
        const inputName = `q_${question.pk}`;
        const inputHasFormula = question.formula.length > 0;
        const inputType = question.question_type.question_type.toLowerCase();

        const isInputDisabled =
          inputHasFormula ||
          question.read_only ||
          (question.only_write_once && defaultValue ? true : false);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const inputFinalValue = useMemo(() => {
          const defaultValue = getFormattedDefaultValue(
            responseMapper[inputName] || question.default,
            inputType,
            question.multiple_choices
          );
          if (inputHasFormula) {
            const newFinalValue = processFormula(
              question,
              defaultValue,
              { ...responseMapper, ...responseFormulaMapper },
              setResponseFormulaMapper
            );
            return `${newFinalValue}`;
          } else return defaultValue;
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [responseMapper, historySelected]);

        let inputKey = `${inputName}`;
        if (inputHasFormula || historySelected) {
          inputKey = `${inputName}_${inputFinalValue}`;
        }

        const minValue = processMinMaxValue(
          question.questioncondition_set?.[0]?.min
        );
        const maxValue = processMinMaxValue(
          question.questioncondition_set?.[0]?.max
        );

        return (
          <InputHandler
            key={inputKey}
            type={inputType}
            control={surveyForm.control}
            name={inputName}
            label={question.question_text}
            labelHelper={question.question_description ?? undefined}
            filePreSelected={preloadedFile[inputKey]}
            placeholder={question.question_text}
            options={optionsInput}
            defaultValue={inputFinalValue}
            isMulti={question.multiple_choices}
            disabled={
              isInputDisabled || (inputType === 'dropzone' && loadingPresigned)
            }
            isLoading={inputType === 'dropzone' && loadingPresigned}
            onInputChange={async (search) => {
              const option_text = search ?? '';
              if (inputType === 'select' && question.search_option) {
                getSelectQuestionOptions(
                  option_text,
                  question.pk,
                  setOptionsInput
                );
              }
              if (inputType !== 'select') {
                setResponseMapper((prev) => ({ ...prev, [inputName]: search }));
              }
              if (inputType === 'dropzone') {
                const file = await presignedFile(
                  APIS.UFCA.PRESIGNED_ANSWER,
                  search,
                  setLoadingPresigned
                );
                setResponseMapper((prev) => ({ ...prev, [inputName]: file }));
                setPreloadedFile((prev) => ({
                  ...prev,
                  [inputName]: file ? search : null
                }));
              }
              setFillingSurvey && setFillingSurvey(true);
            }}
            onEventChange={(option) => {
              if (question.multiple_choices) {
                if (option.length > 0) {
                  setResponseMapper((prev) => {
                    const responses = option
                      .map((item) => item.value)
                      .join(',');
                    return { ...prev, [inputName]: responses };
                  });
                }
              } else {
                setResponseMapper((prev) => ({
                  ...prev,
                  [inputName]: option.value
                }));
              }
              setFillingSurvey && setFillingSurvey(true);
            }}
            onFocus={() => {
              if (inputType === 'select') {
                if (question.option_set.length > 0) {
                  const options = question.option_set.map((option) => ({
                    label: option.option_text,
                    value: option.option_text
                  }));
                  setOptionsInput([EMPTY_SELECT, ...options]);
                } else if (
                  question.option_set.length === 0 &&
                  optionsInput.length > 0
                ) {
                  setOptionsInput([EMPTY_SELECT]);
                }
              }
            }}
            onBlur={() => {
              if (optionsInput.length > 0) {
                setOptionsInput([EMPTY_SELECT]);
              }
            }}
            min={minValue}
            max={maxValue}
            rules={{
              ...(question.required ? INPUT_REQUIRED : undefined),
              validate:
                question?.questioncondition_set?.length > 0
                  ? generateRegexValidators(question.questioncondition_set)
                  : undefined,
              ...(minValue && {
                min: {
                  value: minValue,
                  message: tnl('labels.min_value', {
                    minValue: minValue
                  })
                }
              }),
              ...(maxValue && {
                max: {
                  value: maxValue,
                  message: tnl('labels.max_value', {
                    maxValue: maxValue
                  })
                }
              })
            }}
            step={inputType === 'number' ? '0.01' : undefined}
            design="col-md-3"
          />
        );
      })}
    </div>
  );
};

SurveyQuestions.propTypes = {
  questionsData: PropTypes.arrayOf(PropTypes.object).isRequired,
  surveyForm: PropTypes.object.isRequired,
  responseMapper: PropTypes.object.isRequired,
  responseFormulaMapper: PropTypes.object.isRequired,
  setResponseMapper: PropTypes.func.isRequired,
  setResponseFormulaMapper: PropTypes.func.isRequired,
  setFillingSurvey: PropTypes.func,
  preloadedFile: PropTypes.object,
  setPreloadedFile: PropTypes.func
};

export default SurveyQuestions;
